import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const contrastiveLossComponent: ComponentDef = {
  id: 'contrastive-loss',
  name: 'Contrastive Loss',
  description: 'Self-supervised similarity learning: InfoNCE/NT-Xent pull anchor+positive together and push negatives apart. Used in CLIP, SimCLR, and text embedding models.',
  category: 'function',
  params: [
    { name: 'd_model', label: 'Embedding dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'batch_size', label: 'Batch size (N pairs)', type: 'number', default: 64, min: 2, max: 1024, step: 2 },
    {
      name: 'variant',
      label: 'Loss variant',
      type: 'select',
      default: 'infonce',
      options: [
        { label: 'InfoNCE (self-supervised, in-batch negatives)', value: 'infonce' },
        { label: 'NT-Xent (SimCLR, symmetric)', value: 'nt-xent' },
        { label: 'Triplet (anchor, positive, negative)', value: 'triplet' }
      ]
    },
    { name: 'temperature', label: 'Temperature τ', type: 'number', default: 0.07, min: 0.01, max: 1.0, step: 0.01 }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const batch_size = Number(params.batch_size);
    const variant = String(params.variant || 'infonce');

    const g = new GraphBuilder();

    if (variant === 'triplet') {
      g.input('anchor', [batch_size, d_model]);
      g.input('positive', [batch_size, d_model]);
      g.input('negative', [batch_size, d_model]);

      // L2-normalize embeddings
      g.norm('anchor_n', 'anchor', 'L2-norm');
      g.norm('pos_n', 'positive', 'L2-norm');
      g.norm('neg_n', 'negative', 'L2-norm');

      // Euclidean distance: d(a,p) and d(a,n)
      g.add('diff_pos', 'anchor_n', 'pos_n');
      g.add('diff_neg', 'anchor_n', 'neg_n');
      g.activation('d_pos', 'diff_pos', '||a − p||₂');
      g.activation('d_neg', 'diff_neg', '||a − n||₂');

      // Triplet loss: max(0, d(a,p) − d(a,n) + margin)
      g.add('margin_diff', 'd_pos', 'd_neg');
      g.activation('loss', 'margin_diff', 'max(0, d_ap − d_an + margin)');
      g.output('out', 'loss');

    } else {
      // InfoNCE and NT-Xent share the similarity matrix approach
      g.input('z_i', [batch_size, d_model]);  // anchor / view 1
      g.input('z_j', [batch_size, d_model]);  // positive / view 2

      // L2-normalize embeddings
      g.norm('zi_n', 'z_i', 'L2-norm');
      g.norm('zj_n', 'z_j', 'L2-norm');

      // Similarity matrix: [N, d] × [d, N] → [N, N]; diagonal = positives
      g.transpose('zj_T', 'zj_n', [d_model, batch_size], 'Z_j^T');
      g.matmul('sim_matrix', 'zi_n', 'zj_T', [batch_size, batch_size]);
      g.scale('sim_scaled', 'sim_matrix', '÷ τ  (temperature scaling)');

      if (variant === 'nt-xent') {
        // NT-Xent: symmetric — compute loss in both directions
        g.transpose('sim_T', 'sim_scaled', [batch_size, batch_size], 'Symmetric');
        g.softmax('p_ij', 'sim_scaled', -1);
        g.softmax('p_ji', 'sim_T', -1);
        // Cross-entropy on diagonal (positive pairs)
        g.activation('ce_ij', 'p_ij', '−log p(j|i)  [positive pair diagonal]');
        g.activation('ce_ji', 'p_ji', '−log p(i|j)  [symmetric]');
        g.add('loss', 'ce_ij', 'ce_ji');
        g.scale('loss_mean', 'loss', '÷ 2N  (mean over N pairs)');
        g.output('out', 'loss_mean');
      } else {
        // InfoNCE: one direction; positive = (i, j), negatives = all other j in batch
        g.softmax('probs', 'sim_scaled', -1);
        // Loss = −log p(positive_j | anchor_i) averaged over batch
        g.activation('loss', 'probs', '−log p(j_pos | i)  [InfoNCE]');
        g.output('out', 'loss');
      }
    }

    return g.build();
  }
};
